<template>
  <div class="pressure-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$p = F / S$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真演示区 -->
      <div class="simulation-stage" ref="canvasContainer">
        <!-- 实时数据叠加层 -->
        <div class="pressure-hud">
          <div class="hud-item stress">
            <label>平均压强 p</label>
            <span class="val">{{ pressure.toFixed(1) }} <small>Pa</small></span>
          </div>
          <div class="hud-item sink">
            <label>形变深度</label>
            <span class="val">{{ (pressure / 10).toFixed(2) }} <small>mm</small></span>
          </div>
        </div>
        
        <div class="view-selector">
          <el-radio-group v-model="viewMode" size="mini">
            <el-radio-button label="vertical">针尖向下</el-radio-button>
            <el-radio-button label="flat">平面向下</el-radio-button>
          </el-radio-group>
        </div>
      </div>

      <!-- 参数控制区 -->
      <div class="compact-controls">
        <div class="control-card-grid">
           <div class="control-item">
              <div class="label-row">
                 <span class="label">施加压力 (F)</span>
                 <span class="val">{{ force }} N</span>
              </div>
              <el-slider v-model="force" :min="10" :max="200" size="small"></el-slider>
           </div>
           
           <div class="control-item area-preview">
              <div class="label-row">
                 <span class="label">受力面积 (S)</span>
                 <span class="val">{{ area }} cm²</span>
              </div>
              <div class="area-visualizer">
                 <div class="area-square" :style="{ width: Math.sqrt(area)*6 + 'px', height: Math.sqrt(area)*6 + 'px' }"></div>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-warning-outline"></i>
           <p><strong>关键观察：</strong>压力相同时，受力面积越小（如针尖），压强越大。同样的力在海绵上造成的形变效果截然不同，这解释了为何利刃更容易切开物体。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'PressureViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      force: 60,
      viewMode: 'flat',
      running: true
    }
  },
  computed: {
    area() { return this.viewMode === 'flat' ? 25 : 2; },
    pressure() { return this.force / this.area; }
  },
  watch: {
    force() { this.updateGeometry(); },
    viewMode() { this.updateGeometry(); }
  },
  mounted() { this.initThree(); },
  beforeDestroy() {
    this.running = false;
    cancelAnimationFrame(this.frameId);
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
  },
  methods: {
    initThree() {
      const container = this.$refs.canvasContainer;
      const width = container.clientWidth;
      const height = container.clientHeight;

      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xf1f5f9);

      this.camera = new THREE.PerspectiveCamera(40, width / height, 1, 1000);
      this.camera.position.set(50, 45, 90);
      this.camera.lookAt(0, 5, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      container.appendChild(this.renderer.domElement);

      // Sponge Base
      const spongeGeo = new THREE.BoxGeometry(70, 12, 70);
      const spongeMat = new THREE.MeshPhongMaterial({ color: 0xfde047 });
      this.sponge = new THREE.Mesh(spongeGeo, spongeMat);
      this.sponge.position.y = -6;
      this.scene.add(this.sponge);

      // Pressing Object
      this.pressObject = new THREE.Mesh();
      this.scene.add(this.pressObject);
      this.objectMat = new THREE.MeshPhongMaterial({ color: 0x2563eb, specular: 0x60a5fa });
      this.pressObject.material = this.objectMat;

      const light = new THREE.DirectionalLight(0xffffff, 1);
      light.position.set(30, 80, 50);
      this.scene.add(light);
      this.scene.add(new THREE.AmbientLight(0xffffff, 0.7));

      this.updateGeometry();
      this.animate();
    },
    updateGeometry() {
      if (this.pressObject.geometry) this.pressObject.geometry.dispose();
      this.pressObject.geometry = this.viewMode === 'flat' 
        ? new THREE.BoxGeometry(25, 30, 25) 
        : new THREE.ConeGeometry(8, 30, 24);
      
      const sink = this.pressure / 8;
      this.pressObject.position.y = 15 - sink;
    },
    animate() {
      if (!this.running) return;
      this.frameId = requestAnimationFrame(this.animate);
      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.pressure-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 220px; background: #ffffff; border-radius: 12px; border: 1.5px solid #e2e8f0; position: relative; overflow: hidden;
  .pressure-hud {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: #ffffff; border-radius: 8px; border: 1px solid #e2e8f0; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 14px; color: #1e293b; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #64748b; font-weight: normal; } }
      &.stress { border-left: 3px solid #ef4444; .val { color: #ef4444; } }
      &.sink { border-left: 3px solid #3b82f6; .val { color: #3b82f6; } }
    }
  }
  .view-selector { position: absolute; bottom: 12px; right: 12px; z-index: 10; }
}

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card-grid {
  display: grid; grid-template-columns: 1.2fr 1fr; gap: 12px; background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0;
  .control-item {
    .label-row { display: flex; justify-content: space-between; .label { font-size: 12px; color: #64748b; } .val { font-size: 12px; color: #2563eb; font-weight: 700; } }
    &.area-preview { border-left: 1px solid #f1f5f9; padding-left: 12px; }
  }
}
.area-visualizer { 
  height: 40px; display: flex; align-items: center; justify-content: center; background: #f8fafc; border-radius: 6px; margin-top: 4px;
  .area-square { background: #fde047; border: 1px solid #eab308; box-shadow: 0 0 8px rgba(234, 179, 8, 0.2); transition: all 0.3s ease; }
}

.discovery-card-compact {
  background: #fff7ed; padding: 10px 14px; border-radius: 10px; border: 1px solid #ffedd5; display: flex; align-items: center; gap: 10px;
  i { color: #f59e0b; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #9a3412; line-height: 1.5; strong { color: #7c2d12; } }
}
</style>
